import { useState, useEffect } from "react";
import {
  FaEye,
  FaEyeSlash,
  FaGripVertical,
  FaSlidersH,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { LoadingBox } from "./Box";
import "./BoxCustomizer.scss";

// ─── Static box definitions ───────────────────────────────────────────────────
const BOX_DEFINITIONS = [
  {
    id: "documents_required",
    link: "shipments?filter=documents-required",
    text: "Documents Required",
  },
  {
    id: "forms_7501",
    link: "shipments?filter=7501-review",
    text: "7501 Forms to Review",
  },
  {
    id: "cbp_hold",
    link: "shipments?filter=under-cbp-exam",
    text: "CBP Hold/Exam",
  },
  {
    id: "fda_hold",
    link: "shipments?filter=under-fda-exam",
    text: "FDA Hold/Exam",
  },
  {
    id: "without_do",
    link: "shipments?filter=without-do",
    text: "Without\nDelivery Order",
  },
  {
    id: "pending_review",
    link: "shipments?filter=pending-review",
    text: "Pending Review",
  },
  {
    id: "missing_info",
    link: "shipments?filter=missing-info",
    text: "Missing Info",
  },
];

const DEFAULT_LAYOUT = BOX_DEFINITIONS.map((b, i) => ({
  id: b.id,
  visible: true,
  order: i,
}));

// ─── BoxCustomizer ────────────────────────────────────────────────────────────
// Props:
//   counts      – { [id]: number | undefined }
//   countsReady – boolean: true once the shipments fetch resolved
//   token       – Bearer token string
//   apiBase     – base URL string (e.g. process.env.REACT_APP_API_URL + "/user")
const BoxCustomizer = ({ counts, countsReady, token, apiBase }) => {
  const [layoutItems, setLayoutItems] = useState(DEFAULT_LAYOUT);
  const [lastSaved, setLastSaved] = useState(DEFAULT_LAYOUT);
  const [customizing, setCustomizing] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── Fetch saved layout on mount ─────────────────────────────────────────────
  useEffect(() => {
    if (!token || !apiBase) return;
    const fetchLayout = async () => {
      try {
        const res = await fetch(`${apiBase}/box-layout`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        if (res.ok) {
          const data = await res.json();
          if (
            data.layout &&
            Array.isArray(data.layout) &&
            data.layout.length > 0
          ) {
            const sanitizedLayout = data.layout.map(i => ({ ...i, visible: true }));
            const savedIds = sanitizedLayout.map((i) => i.id);
            const missing = DEFAULT_LAYOUT.filter(
              (d) => !savedIds.includes(d.id)
            ).map((d, idx) => ({ ...d, order: sanitizedLayout.length + idx }));
            const merged = [...sanitizedLayout, ...missing];
            setLayoutItems(merged);
            setLastSaved(merged);
          }
        }
      } catch {
        // silently fall back to default
      }
    };
    fetchLayout();
  }, [token, apiBase]);

  // ── Derived: sorted, role-filtered list ────────────────────────────────────
  const sorted = [...layoutItems].sort((a, b) => a.order - b.order);
  const roleFiltered = countsReady
    ? sorted.filter((item) => counts[item.id] !== undefined)
    : sorted;

  // ── Drag handlers ───────────────────────────────────────────────────────────
  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== dragOverId) setDragOverId(id);
  };
  const handleDrop = (e, dropId) => {
    e.preventDefault();
    if (!dragId || dragId === dropId) {
      setDragId(null);
      setDragOverId(null);
      return;
    }
    setLayoutItems((prev) => {
      const items = prev.map((i) => ({ ...i }));
      const dragItem = items.find((i) => i.id === dragId);
      const dropItem = items.find((i) => i.id === dropId);
      if (!dragItem || !dropItem) return prev;
      const tmp = dragItem.order;
      dragItem.order = dropItem.order;
      dropItem.order = tmp;
      return items;
    });
    setDragId(null);
    setDragOverId(null);
  };
  const handleDragEnd = () => {
    setDragId(null);
    setDragOverId(null);
  };

  // ── Visibility toggle ───────────────────────────────────────────────────────
  const handleToggleVisible = (id) => {
    setLayoutItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, visible: !item.visible } : item
      )
    );
  };

  // ── Save / Cancel ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const toSave = [...layoutItems].sort((a, b) => a.order - b.order);
      const res = await fetch(`${apiBase}/box-layout`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ layout: toSave }),
      });
      if (res.ok) {
        setLastSaved([...layoutItems]);
        setCustomizing(false);
        toast.success("Layout saved!");
      } else {
        toast.error("Could not save layout. Please try again.");
      }
    } catch {
      toast.error("Could not save layout. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setLayoutItems([...lastSaved]);
    setCustomizing(false);
  };

  // ── NORMAL MODE ─────────────────────────────────────────────────────────────
  if (!customizing) {
    const visibleBoxes = roleFiltered.filter((item) => item.visible);

    return (
      <div className="boxes-section">
        <div className="boxes">
          {!countsReady
            ? DEFAULT_LAYOUT.map((item) => <LoadingBox key={item.id} />)
            : visibleBoxes.map((item) => {
                const def = BOX_DEFINITIONS.find((d) => d.id === item.id);
                if (!def) return null;
                const lines = String(def.text).split("\n");
                return (
                  <a
                    key={item.id}
                    href={def.link}
                    className="box"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = `/${def.link}`;
                    }}
                  >
                    <div className="box-numb">
                      <p>{counts[item.id]}</p>
                    </div>
                    <div className="box-text">
                      {lines.map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))}
                    </div>
                  </a>
                );
              })}
        </div>

        {countsReady && (
          <div className="boxes-actions-row">
            <button
              className="boxes-customize-btn"
              onClick={() => setCustomizing(true)}
              title="Customize dashboard boxes"
              aria-label="Customize dashboard boxes"
            >
              <FaSlidersH />
              Customize
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── CUSTOMIZE MODE ──────────────────────────────────────────────────────────
  return (
    <div className="boxes-section boxes-section--customizing">
      <div className="boxes-customize-header">
        <span className="boxes-customize-hint">
          Drag{" "}
          <FaGripVertical
            style={{ verticalAlign: "middle", fontSize: "11px" }}
          />{" "}
          to reorder
        </span>
        <div className="boxes-customize-actions">
          <button
            className="boxes-customize-cancel-btn"
            onClick={handleCancel}
            disabled={saving}
          >
            <FaTimes />
            Cancel
          </button>
          <button
            className="boxes-customize-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            <FaCheck />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="boxes boxes--customizing">
        {roleFiltered.map((item) => {
          const def = BOX_DEFINITIONS.find((d) => d.id === item.id);
          if (!def) return null;
          const lines = String(def.text).split("\n");
          const isDragOver = dragOverId === item.id;
          const isBeingDragged = dragId === item.id;

          return (
            <div
              key={item.id}
              className={[
                "box box--customizable",
                !item.visible ? "box--hidden" : "",
                isDragOver ? "box--drag-over" : "",
                isBeingDragged ? "box--dragging" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              draggable
              onDragStart={(e) => handleDragStart(e, item.id)}
              onDragOver={(e) => handleDragOver(e, item.id)}
              onDrop={(e) => handleDrop(e, item.id)}
              onDragEnd={handleDragEnd}
            >
              <div className="box__drag-handle" title="Drag to reorder">
                <FaGripVertical />
              </div>

              <div className="box-numb">
                <p>{counts[item.id] ?? "–"}</p>
              </div>
              <div className="box-text">
                {lines.map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
              </div>

              {!item.visible && (
                <div className="box__hidden-badge">Hidden</div>
              )}


            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BoxCustomizer;
